import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

// Cache references to the containing-block-breaking ancestors
function getHeaderFixTargets() {
  return {
    stickyHeader: document.querySelector('.shopify-section-group-header-group'),
    headerComponent: document.querySelector('header-component')
  };
}

// Force the sticky header and contain:layout out of the way so that
// position:fixed on the drawer resolves to the viewport, not the document.
function fixHeaderContainingBlock() {
  const { stickyHeader, headerComponent } = getHeaderFixTargets();
  if (headerComponent) headerComponent.style.contain = 'none';
  if (stickyHeader) {
    stickyHeader.style.position = 'fixed';
    stickyHeader.style.top = '0';
    stickyHeader.style.left = '0';
    stickyHeader.style.right = '0';
    stickyHeader.style.width = '100%';
    stickyHeader.style.transform = 'none';
    stickyHeader.style.willChange = 'auto';
  }
}

function restoreHeaderContainingBlock() {
  const { stickyHeader, headerComponent } = getHeaderFixTargets();
  if (headerComponent) headerComponent.style.contain = '';
  if (stickyHeader) {
    stickyHeader.style.position = '';
    stickyHeader.style.top = '';
    stickyHeader.style.left = '';
    stickyHeader.style.right = '';
    stickyHeader.style.width = '';
    stickyHeader.style.transform = '';
    stickyHeader.style.willChange = '';
  }
}

class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() { return this.refs.details.hasAttribute('open'); }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() { return this.isOpen ? this.close() : this.open(); }

  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);
    // Fix: remove sticky compositing layer & contain:layout containing block
    // so the drawer's position:fixed resolves to the viewport, not the document.
    if (details === this.refs.details) fixHeaderContainingBlock();
    // Double rAF: ensures iOS processes the layout change before drawer animates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
        onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
      });
    });
  }

  back(event) { this.#close(this.#getDetailsElement(event)); }
  close() { this.#close(this.refs.details); }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    const safetyTimeout = setTimeout(() => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        restoreHeaderContainingBlock();
      } else {
        trapFocus(this.refs.details);
      }
    }, 400);

    onAnimationEnd(drawer || details, () => {
      clearTimeout(safetyTimeout);
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        restoreHeaderContainingBlock();
      } else {
        trapFocus(this.refs.details);
      }
    }, { subtree: false });
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach((el) => { if (el instanceof HTMLElement) el.classList.add('details-content--no-animation'); });
    setTimeout(() => {
      content.forEach((el) => { if (el instanceof HTMLElement) el.classList.remove('details-content--no-animation'); });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
